// Settings Modal — persists preferences via /api/user/settings + localStorage audio.
// Layout: sticky header · scroll body · sticky footer (no double scroll / clipped actions).

import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Volume2,
  Palette,
  Shield,
  User,
  Loader2,
  Settings2,
  Mic,
  Camera,
  Headphones,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiUrl } from "@/lib/api";
import { casino } from "@/lib/casino-sfx";
import { BRAND } from "@shared/brand";
import { cn } from "@/lib/utils";
import {
  listMediaDevices,
  loadMediaDevicePrefs,
  saveMediaDevicePrefs,
  requestMediaPermissions,
  supportsSetSinkId,
  type MediaDeviceChoice,
  type MediaDevicePrefs,
} from "@/lib/media-devices";

export interface GameSettings {
  displayName: string;
  soundEnabled: boolean;
  soundVolume: number;
  /** Soft BGM (BUDB / MTT Soldier) */
  musicEnabled: boolean;
  musicVolume: number;
  animationsEnabled: boolean;
  autoMuckEnabled: boolean;
  showOdds: boolean;
  /** Felt color: green | red | blue */
  tableTheme: string;
  /** Modal / shell plate: felt | wood */
  backgroundTheme: string;
  chatEnabled: boolean;
  notifications: boolean;
}

const DEFAULT_SETTINGS: GameSettings = {
  displayName: "",
  soundEnabled: true,
  soundVolume: 75,
  musicEnabled: true,
  musicVolume: 35,
  animationsEnabled: true,
  autoMuckEnabled: false,
  showOdds: true,
  tableTheme: "green",
  backgroundTheme: "felt",
  chatEnabled: true,
  notifications: true,
};

const TABLE_THEMES = [
  { value: "green", label: "Classic green" },
  { value: "red", label: "Red felt" },
  { value: "blue", label: "Blue felt" },
] as const;

const PANEL_THEMES = [
  {
    value: "felt",
    label: "Suit pattern",
    url: BRAND.panels.feltPattern,
  },
  {
    value: "wood",
    label: "Wood grain",
    url: BRAND.panels.wood,
  },
] as const;

/** Normalize legacy values from older clients / D1 JSON */
function normalizeSettings(raw: Partial<GameSettings> & Record<string, unknown>): GameSettings {
  const tableMap: Record<string, string> = {
    classic: "green",
    luxury: "red",
    modern: "blue",
    royal: "blue",
    green: "green",
    red: "red",
    blue: "blue",
    violet: "blue",
  };
  const bgMap: Record<string, string> = {
    poker1: "felt",
    poker2: "wood",
    felt: "felt",
    wood: "wood",
  };
  const tableTheme = tableMap[String(raw.tableTheme ?? "")] ?? DEFAULT_SETTINGS.tableTheme;
  const backgroundTheme =
    bgMap[String(raw.backgroundTheme ?? "")] ?? DEFAULT_SETTINGS.backgroundTheme;

  return {
    ...DEFAULT_SETTINGS,
    displayName: typeof raw.displayName === "string" ? raw.displayName : "",
    soundEnabled: raw.soundEnabled !== false,
    soundVolume: clampVol(Number(raw.soundVolume)),
    musicEnabled: raw.musicEnabled !== false,
    musicVolume: clampVol(
      Number.isFinite(Number(raw.musicVolume))
        ? Number(raw.musicVolume)
        : DEFAULT_SETTINGS.musicVolume,
    ),
    animationsEnabled: raw.animationsEnabled !== false,
    autoMuckEnabled: !!raw.autoMuckEnabled,
    showOdds: raw.showOdds !== false,
    tableTheme,
    backgroundTheme,
    chatEnabled: raw.chatEnabled !== false,
    notifications: raw.notifications !== false,
  };
}

function clampVol(n: number): number {
  if (!Number.isFinite(n)) return 75;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function persistAudio(
  settings: Pick<
    GameSettings,
    "soundEnabled" | "soundVolume" | "musicEnabled" | "musicVolume"
  >,
) {
  try {
    const raw = localStorage.getItem("poker_settings");
    const cur = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    cur.soundEnabled = settings.soundEnabled;
    cur.soundVolume = settings.soundVolume;
    cur.musicEnabled = settings.musicEnabled;
    cur.musicVolume = settings.musicVolume;
    localStorage.setItem("poker_settings", JSON.stringify(cur));
    window.dispatchEvent(new CustomEvent("poker-settings-changed"));
  } catch {
    /* ignore */
  }
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsChange?: (settings: GameSettings) => void;
  /** Wallet address — enables D1 persistence */
  walletAddress?: string;
}

export function SettingsModal({
  isOpen,
  onClose,
  onSettingsChange,
  walletAddress,
}: SettingsModalProps) {
  const [settings, setSettings] = useState<GameSettings>(DEFAULT_SETTINGS);
  const [mediaPrefs, setMediaPrefs] = useState<MediaDevicePrefs>(() =>
    loadMediaDevicePrefs(),
  );
  const [devices, setDevices] = useState<MediaDeviceChoice[]>([]);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaErr, setMediaErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const sinkOk = supportsSetSinkId();

  const refreshDevices = useCallback(async () => {
    try {
      const list = await listMediaDevices();
      setDevices(list);
    } catch (e) {
      setMediaErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const requestMic = useCallback(async () => {
    setMediaBusy(true);
    setMediaErr(null);
    const r = await requestMediaPermissions({ video: false });
    setMediaBusy(false);
    if (!r.ok) {
      setMediaErr(r.error || "Permission denied");
      toast({
        title: "Microphone blocked",
        description: r.error || "Allow mic in browser site settings",
        variant: "destructive",
      });
      return;
    }
    await refreshDevices();
    toast({
      title: "Microphone ready",
      description: "Pick headset / mic below for Vidu voice chat",
    });
  }, [refreshDevices, toast]);

  const panelUrl = useMemo(() => {
    const hit = PANEL_THEMES.find((p) => p.value === settings.backgroundTheme);
    return hit?.url ?? BRAND.panels.feltPattern;
  }, [settings.backgroundTheme]);

  useEffect(() => {
    if (!isOpen) return;
    setMediaPrefs(loadMediaDevicePrefs());
    void refreshDevices();

    // Local audio prefs first (instant)
    try {
      const raw = localStorage.getItem("poker_settings");
      if (raw) {
        const local = JSON.parse(raw) as Partial<GameSettings>;
        setSettings((prev) =>
          normalizeSettings({
            ...prev,
            soundEnabled: local.soundEnabled,
            soundVolume: local.soundVolume,
            musicEnabled: local.musicEnabled,
            musicVolume: local.musicVolume,
          }),
        );
      }
    } catch {
      /* ignore */
    }

    if (!walletAddress) return;

    let cancelled = false;
    setLoading(true);
    fetch(apiUrl(`/api/user/settings?wallet=${encodeURIComponent(walletAddress)}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        // API may nest under settings or return flat fields
        const payload =
          data.settings && typeof data.settings === "object"
            ? { ...data.settings, displayName: data.username ?? data.settings.displayName }
            : data;
        setSettings(normalizeSettings(payload as Partial<GameSettings>));
      })
      .catch(() => {
        /* non-fatal */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, walletAddress, refreshDevices]);

  const updateMediaPref = <K extends keyof MediaDevicePrefs>(
    key: K,
    value: MediaDevicePrefs[K],
  ) => {
    setMediaPrefs((prev) => {
      const next = { ...prev, [key]: value };
      saveMediaDevicePrefs(next);
      return next;
    });
  };

  const mics = devices.filter((d) => d.kind === "audioinput");
  const speakers = devices.filter((d) => d.kind === "audiooutput");
  const cameras = devices.filter((d) => d.kind === "videoinput");

  const updateSetting = <K extends keyof GameSettings>(key: K, value: GameSettings[K]) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      onSettingsChange?.(next);
      if (
        key === "soundEnabled" ||
        key === "soundVolume" ||
        key === "musicEnabled" ||
        key === "musicVolume"
      ) {
        persistAudio(next);
      }
      return next;
    });
    if (key === "soundEnabled" && value === true) {
      casino.unlock();
      casino.bet();
    }
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    onSettingsChange?.(DEFAULT_SETTINGS);
    persistAudio(DEFAULT_SETTINGS);
  };

  const handleSave = async () => {
    persistAudio(settings);

    if (walletAddress) {
      setSaving(true);
      try {
        const res = await fetch(apiUrl("/api/user/settings"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ wallet: walletAddress, settings }),
        });
        if (!res.ok) throw new Error("Server error");
        const data = await res.json();
        if (data.settings) {
          setSettings(
            normalizeSettings({
              ...data.settings,
              displayName: data.username || settings.displayName,
            }),
          );
        }
        toast({ title: "Settings saved", description: "Preferences synced to your wallet." });
      } catch {
        toast({
          title: "Saved locally",
          description: "Could not reach server — audio prefs kept on this device.",
          variant: "destructive",
        });
      } finally {
        setSaving(false);
      }
    } else {
      toast({
        title: "Saved on this device",
        description: "Connect a wallet to sync profile across sessions.",
      });
    }
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={cn(
          "settings-modal p-0 gap-0 overflow-hidden border border-amber-500/25",
          "w-[min(100vw-1.25rem,28rem)] sm:max-w-md",
          "max-h-[min(90dvh,40rem)]",
          "bg-transparent shadow-2xl",
          // Hide default Dialog X — header owns close via Radix close is still keyboard-accessible via Escape
          "[&>button.absolute]:hidden",
        )}
      >
        <div
          className="settings-modal__shell flex flex-col max-h-[min(90dvh,40rem)] rounded-lg overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(165deg, rgba(10,8,6,0.92), rgba(6,5,4,0.96)), url(${panelUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* ── Sticky header ─────────────────────────────────────────── */}
          <DialogHeader className="settings-modal__header shrink-0 px-4 pt-4 pb-3 border-b border-amber-500/20 text-left space-y-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle className="text-lg font-bold text-amber-50 flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-amber-400 shrink-0" />
                  Game Settings
                </DialogTitle>
                <DialogDescription className="text-xs text-amber-100/55 mt-0.5">
                  {walletAddress
                    ? "Synced when you save · wallet profile"
                    : "Local on this device · connect wallet to sync"}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* ── Scroll body only ──────────────────────────────────────── */}
          <div
            className="settings-modal__body flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-7 w-7 text-amber-400 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4 pb-1">
                {/* Profile */}
                <section className="settings-section">
                  <h3 className="settings-section__title">
                    <User className="h-3.5 w-3.5" />
                    Profile
                  </h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-display-name" className="text-amber-50/90 text-sm">
                      Display name
                    </Label>
                    <Input
                      id="settings-display-name"
                      value={settings.displayName}
                      onChange={(e) => updateSetting("displayName", e.target.value)}
                      placeholder="Shown at the table"
                      maxLength={30}
                      autoComplete="nickname"
                      className="settings-input h-10"
                    />
                  </div>
                </section>

                {/* Devices — mic / speaker / camera for Vidu voice chat */}
                <section className="settings-section">
                  <h3 className="settings-section__title">
                    <Headphones className="h-3.5 w-3.5" />
                    Headset · mic · camera
                  </h3>
                  <p className="text-[11px] text-zinc-500 mb-2">
                    Used by BetUDontBet table talk and Racalvin blackjack dealer
                    (Vidu share-avatar needs mic permission in the iframe).
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-amber-500/40 mb-3"
                    disabled={mediaBusy}
                    onClick={() => void requestMic()}
                  >
                    {mediaBusy ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Mic className="h-3.5 w-3.5 mr-1" />
                    )}
                    Allow microphone
                  </Button>
                  {mediaErr && (
                    <p className="text-[11px] text-red-300 mb-2">{mediaErr}</p>
                  )}
                  <div className="space-y-2 pt-1">
                    <Label className="text-sm text-amber-50/90 flex items-center gap-1.5">
                      <Mic className="h-3.5 w-3.5" /> Microphone
                    </Label>
                    <Select
                      value={mediaPrefs.audioInputId || "default"}
                      onValueChange={(v) =>
                        updateMediaPref(
                          "audioInputId",
                          v === "default" ? null : v,
                        )
                      }
                    >
                      <SelectTrigger className="settings-input h-10">
                        <SelectValue placeholder="System default" />
                      </SelectTrigger>
                      <SelectContent className="z-[60]">
                        <SelectItem value="default">System default</SelectItem>
                        {mics.map((d) => (
                          <SelectItem key={d.deviceId} value={d.deviceId}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm text-amber-50/90 flex items-center gap-1.5">
                      <Volume2 className="h-3.5 w-3.5" /> Speakers / headset out
                    </Label>
                    <Select
                      value={mediaPrefs.audioOutputId || "default"}
                      onValueChange={(v) =>
                        updateMediaPref(
                          "audioOutputId",
                          v === "default" ? null : v,
                        )
                      }
                      disabled={!sinkOk}
                    >
                      <SelectTrigger className="settings-input h-10">
                        <SelectValue
                          placeholder={
                            sinkOk ? "System default" : "Browser default only"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="z-[60]">
                        <SelectItem value="default">System default</SelectItem>
                        {speakers.map((d) => (
                          <SelectItem key={d.deviceId} value={d.deviceId}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!sinkOk && (
                      <p className="text-[10px] text-zinc-500">
                        Output device picker needs Chrome/Edge (setSinkId).
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm text-amber-50/90 flex items-center gap-1.5">
                      <Camera className="h-3.5 w-3.5" /> Camera
                    </Label>
                    <Select
                      value={mediaPrefs.videoInputId || "default"}
                      onValueChange={(v) =>
                        updateMediaPref(
                          "videoInputId",
                          v === "default" ? null : v,
                        )
                      }
                    >
                      <SelectTrigger className="settings-input h-10">
                        <SelectValue placeholder="System default" />
                      </SelectTrigger>
                      <SelectContent className="z-[60]">
                        <SelectItem value="default">System default</SelectItem>
                        {cameras.map((d) => (
                          <SelectItem key={d.deviceId} value={d.deviceId}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="mt-2 text-zinc-400"
                    onClick={() => void refreshDevices()}
                  >
                    Refresh device list
                  </Button>
                </section>

                {/* Audio */}
                <section className="settings-section">
                  <h3 className="settings-section__title">
                    <Volume2 className="h-3.5 w-3.5" />
                    Audio
                  </h3>
                  <RowToggle
                    label="Sound effects"
                    desc="Casino SFX · slots · UI clicks"
                    checked={settings.soundEnabled}
                    onCheckedChange={(v) => updateSetting("soundEnabled", v)}
                  />
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm text-amber-50/90">SFX volume</Label>
                      <span className="text-xs tabular-nums text-amber-200/70">
                        {settings.soundVolume}%
                      </span>
                    </div>
                    <Slider
                      value={[settings.soundVolume]}
                      onValueChange={(v) => updateSetting("soundVolume", v[0] ?? 0)}
                      max={100}
                      step={5}
                      disabled={!settings.soundEnabled}
                      className="w-full"
                      aria-label="Sound volume"
                    />
                  </div>
                  <RowToggle
                    label="Background music"
                    desc="BUDB soft bed · MTT Soldier lobby · intro stinger"
                    checked={settings.musicEnabled}
                    onCheckedChange={(v) => updateSetting("musicEnabled", v)}
                  />
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm text-amber-50/90">Music volume</Label>
                      <span className="text-xs tabular-nums text-amber-200/70">
                        {settings.musicVolume}%
                      </span>
                    </div>
                    <Slider
                      value={[settings.musicVolume]}
                      onValueChange={(v) => updateSetting("musicVolume", v[0] ?? 0)}
                      max={100}
                      step={5}
                      disabled={!settings.musicEnabled}
                      className="w-full"
                      aria-label="Music volume"
                    />
                  </div>
                </section>

                {/* Look */}
                <section className="settings-section">
                  <h3 className="settings-section__title">
                    <Palette className="h-3.5 w-3.5" />
                    Look
                  </h3>
                  <RowToggle
                    label="Animations"
                    desc="Cards, chips, and reel motion"
                    checked={settings.animationsEnabled}
                    onCheckedChange={(v) => updateSetting("animationsEnabled", v)}
                  />
                  <div className="space-y-1.5 pt-1">
                    <Label className="text-sm text-amber-50/90">Table felt</Label>
                    <Select
                      value={settings.tableTheme}
                      onValueChange={(v) => updateSetting("tableTheme", v)}
                    >
                      <SelectTrigger className="settings-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[60]">
                        {TABLE_THEMES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm text-amber-50/90">Panel style</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {PANEL_THEMES.map((p) => {
                        const active = settings.backgroundTheme === p.value;
                        return (
                          <button
                            key={p.value}
                            type="button"
                            onClick={() => updateSetting("backgroundTheme", p.value)}
                            className={cn(
                              "relative h-16 rounded-lg overflow-hidden border text-left transition",
                              active
                                ? "border-amber-400 ring-2 ring-amber-400/40"
                                : "border-white/15 hover:border-amber-500/40",
                            )}
                            aria-pressed={active}
                          >
                            <span
                              className="absolute inset-0 bg-cover bg-center"
                              style={{ backgroundImage: `url(${p.url})` }}
                              aria-hidden
                            />
                            <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            <span className="absolute bottom-1.5 left-2 right-2 text-[11px] font-semibold text-amber-50 truncate">
                              {p.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* Play */}
                <section className="settings-section">
                  <h3 className="settings-section__title">
                    <Shield className="h-3.5 w-3.5" />
                    Play
                  </h3>
                  <div className="space-y-3">
                    <RowToggle
                      label="Auto-muck losers"
                      desc="Fold / hide losing hands at showdown"
                      checked={settings.autoMuckEnabled}
                      onCheckedChange={(v) => updateSetting("autoMuckEnabled", v)}
                    />
                    <RowToggle
                      label="Show hand odds"
                      desc="Winning probability hints when available"
                      checked={settings.showOdds}
                      onCheckedChange={(v) => updateSetting("showOdds", v)}
                    />
                    <RowToggle
                      label="Table chat"
                      desc="In-table messages"
                      checked={settings.chatEnabled}
                      onCheckedChange={(v) => updateSetting("chatEnabled", v)}
                    />
                    <RowToggle
                      label="Notifications"
                      desc="Turn / result toasts"
                      checked={settings.notifications}
                      onCheckedChange={(v) => updateSetting("notifications", v)}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* ── Sticky footer ─────────────────────────────────────────── */}
          <div className="settings-modal__footer shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-amber-500/20 bg-black/45 backdrop-blur-sm">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetSettings}
              disabled={loading || saving}
              className="text-amber-100/70 hover:text-amber-50 hover:bg-white/5"
            >
              Reset
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={loading || saving}
              className="min-w-[7.5rem] bg-amber-600 hover:bg-amber-500 text-black font-semibold"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RowToggle({
  label,
  desc,
  checked,
  onCheckedChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-0.5">
      <div className="min-w-0 pr-2">
        <p className="text-sm font-medium text-amber-50 leading-snug">{label}</p>
        <p className="text-[11px] text-amber-100/50 leading-snug mt-0.5">{desc}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="shrink-0 mt-0.5"
        aria-label={label}
      />
    </div>
  );
}
